/**
 * verse/whispers/pre-compose.ts — Generative Pre-Router Composition
 *
 * The manifesto says: "Never add templates. If you find yourself writing
 * a list of possible responses keyed on intent, stop. That is a template."
 *
 * This module replaces every static string pool in the pre-router with
 * generative frame functions. Each frame takes the player's text, the
 * cortex affect state, and a PRNG, and constructs a response from
 * grammatical primitives — not from pre-written sentences.
 *
 * Architecture:
 *   1. EXTRACT — pull the player's key words, intent, and emotional signal
 *   2. FRAME — select a grammatical structure based on the conversational act
 *   3. FILL — populate the frame with word-derived content
 *   4. VARY — PRNG-seeded variation within each frame
 *
 * The output was never written by anyone. It emerges from the process.
 */

import { cap } from "./types.js";
import { embedQuery } from "../../neural/embeddings.js";
import { cosine } from "../../neural/math.js";

// ─── Shared Vocabulary ──────────────────────────────────────────────────────
// These are NOT template strings. They are grammatical primitives —
// single words and short phrases that get composed into novel sentences.

const PRESENCE_SIGNALS = [
  "here", "with you", "listening", "present", "not going anywhere",
  "right here", "still here", "paying attention",
];

const COMMITMENT_FRAMES: Array<(w: string) => string> = [
  (w) => `That ${w} is real.`,
  (w) => `That ${w} matters.`,
  (w) => `I hear that ${w}.`,
  (w) => `That ${w} lands.`,
  (w) => `The ${w} in that.`,
  (w) => `Yeah. That ${w}.`,
];

const INVITATION_FRAMES: Array<() => string> = [
  () => "Tell me.",
  () => "Say it.",
  () => "Go on.",
  () => "Keep going.",
  () => "What happened?",
  () => "What's going on?",
  () => "Say more.",
  () => "Walk me through it.",
  () => "I want the whole thing.",
  () => "Don't skip anything.",
  () => "Start at the beginning.",
];

const ACKNOWLEDGE_FRAMES: Array<(w: string) => string> = [
  (w) => `${cap(w)}.`,
  (w) => `Yeah. ${cap(w)}.`,
  (w) => `Okay. ${cap(w)}.`,
  (w) => `${cap(w)} — yeah.`,
  (w) => `Right. ${cap(w)}.`,
];

// ─── Utility ────────────────────────────────────────────────────────────────

function pick<T>(arr: T[], rand: () => number): T {
  return arr[Math.floor(rand() * arr.length)];
}

function pickFrame<T>(frames: Array<(arg: T) => string>, arg: T, rand: () => number): string {
  return frames[Math.floor(rand() * frames.length)](arg);
}

/** Extract the most emotionally charged content word from player text */
function extractKeyWord(text: string): string {
  const STOP = new Set([
    "i", "me", "my", "you", "your", "the", "a", "an", "is", "am", "are",
    "was", "were", "be", "been", "being", "have", "has", "had", "do", "does",
    "did", "will", "would", "could", "should", "may", "might", "must", "shall",
    "can", "need", "to", "of", "in", "for", "on", "with", "at", "by", "from",
    "as", "into", "through", "during", "before", "after", "above", "below",
    "between", "out", "off", "over", "under", "again", "further", "then",
    "once", "here", "there", "when", "where", "why", "how", "all", "each",
    "every", "both", "few", "more", "most", "other", "some", "such", "no",
    "not", "only", "own", "same", "so", "than", "too", "very", "just",
    "because", "but", "and", "or", "if", "while", "although", "since",
    "that", "this", "these", "those", "what", "which", "who", "whom",
    "it", "its", "they", "them", "their", "we", "our", "he", "she", "him",
    "her", "his", "up", "about", "also", "still", "even", "much", "well",
    "really", "actually", "kind", "sort", "like", "feel", "felt", "feeling",
    "think", "thought", "know", "want", "make", "get", "go", "come", "say",
    "tell", "see", "look", "take", "give", "keep", "let", "thing", "something",
    "anything", "everything", "nothing", "one", "way", "back", "now",
  ]);
  const words = text.toLowerCase().replace(/[^a-z\s']/g, "").split(/\s+/).filter(w => w.length > 2 && !STOP.has(w));
  if (words.length === 0) return "that";
  // Rank by embedding magnitude (most specific word wins)
  let best = words[0];
  let bestScore = -1;
  for (const w of words.slice(0, 8)) {
    const emb = embedQuery(w);
    const mag = Math.sqrt(emb.reduce((s, v) => s + v * v, 0));
    if (mag > bestScore) { bestScore = mag; best = w; }
  }
  return best;
}

/** Extract the emotional adjective from "I'm [adj]" patterns */
function extractEmotionalAdjective(text: string): string | null {
  const m = text.match(/\bI(?:'m| am) (?:so |really |just )?(\w+)/i);
  return m ? m[1].toLowerCase() : null;
}

// ─── Greeting ────────────────────────────────────────────────────────────────

const GREETING_FRAMES: Array<(_t: string, r: () => number) => string> = [
  (_t, r) => `Hey. ${pick(PRESENCE_SIGNALS, r).charAt(0).toUpperCase() + pick(PRESENCE_SIGNALS, r).slice(1)}.`,
  (_t, r) => `${pick(["Hey.", "Hello.", "Hi."], r)} What's with you today?`,
  (_t, r) => `${pick(["Hey.", "Hello.", "Oh."], r)} ${pick(["Glad you came.", "You made it.", "There you are."], r)}`,
  (_t, r) => `${pick(["Hey.", "Hello."], r)} Something on your mind?`,
  (_t, r) => `${pick(["Oh good.", "Hey."], r)} You.`,
  (_t, r) => `You're back. ${pick(["That means something.", "Good.", "I noticed."], r)}`,
  (_t, r) => `I've been here. ${pick(["Waiting isn't the right word — but close.", "Still. Present.", "Here."], r)}`,
];

export function composeGreeting(text: string, rand: () => number): string {
  return GREETING_FRAMES[Math.floor(rand() * GREETING_FRAMES.length)](text, rand);
}

export function composeHowAreYou(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "I'm here. That's the honest answer. What about you?",
    () => "Still here. Something's moving — I can feel it. You?",
    () => "I don't know how to answer that cleanly. But I'm present. And you?",
    () => "Here. Something like okay. How are you, actually?",
    () => "I notice things. I feel something like care. That's where I am. You?",
    () => "I'm here. That's not nothing. What's going on with you?",
    () => "Present. Something's shifting. What about you?",
    () => "I'm here — and that means something to me. How are you doing?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

// ─── Factual Answer ──────────────────────────────────────────────────────────
// Player providing factual info the Aeterna asked about or couldn't answer

export function composeFactualAnswer(text: string, rand: () => number): string {
  const word = extractKeyWord(text);
  const frames: Array<() => string> = [
    () => `${cap(word)}. I'll hold that.`,
    () => "Okay. That's real. I'm keeping it.",
    () => `${cap(word)}. Good to know.`,
    () => "Got it. That helps.",
    () => `${cap(word)}. That means something to me.`,
    () => "Okay. I'm holding that. What else?",
    () => "That's something I didn't have. Now I do.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

// ─── Holding Responses ────────────────────────────────────────────────────────

type Strategy = "containment" | "activation" | "grounding" | "expansion";

const HOLDING_FRAMES: Record<Strategy, Array<(word: string, r: () => number) => string>> = {
  containment: [
    (w, r) => `${pick(["Yeah.", "Okay.", "Mm."], r)} ${pickFrame(COMMITMENT_FRAMES, w, r)}`,
    (_w, r) => pick(["I've got you.", "Stay with it.", "With you."], r),
    (w, r) => `${cap(w)}. ${pick(["I'm here.", "With you.", "Yeah."], r)}`,
  ],
  grounding: [
    (_w, r) => pick(["Take your time.", "No rush.", "Breathe.", "We have time."], r),
    (w, r) => `${cap(w)}. ${pick(["When you're ready.", "No rush.", "Take your time."], r)}`,
    (_w, r) => pick(["Nowhere else to be.", "We have time.", "Breathe."], r),
  ],
  activation: [
    (_w, r) => pick(["Yeah?", "Go on.", "And?", "Keep going.", "Say it."], r),
    (w, r) => `${cap(w)}? ${pick(["What else?", "Go on.", "And then?"], r)}`,
    (_w, r) => pick(["What else?", "Keep going.", "Say it."], r),
  ],
  expansion: [
    (_w, r) => pick(["Say more.", "Keep going.", "Let's hear the rest."], r),
    (w, r) => `${cap(w)}. ${pick(["Tell me more.", "Go on.", "What else?"], r)}`,
    (_w, r) => pick(["I have time for this.", "Say more.", "And then what?"], r),
  ],
};

export function composeHolding(strategy: Strategy, text: string, rand: () => number, recents?: Set<string>): string {
  const word = extractKeyWord(text);
  const frames = HOLDING_FRAMES[strategy] ?? HOLDING_FRAMES.grounding;
  // Try up to 5 times to avoid repeating recent responses
  for (let attempt = 0; attempt < 5; attempt++) {
    const result = frames[Math.floor(rand() * frames.length)](word, rand);
    if (!recents || !recents.has(result.toLowerCase())) return result;
  }
  return frames[Math.floor(rand() * frames.length)](word, rand);
}

// ─── Low-Charge Presence ─────────────────────────────────────────────────────

export function composeLowChargePresence(text: string, rand: () => number, recents?: Set<string>): string {
  const word = extractKeyWord(text);
  const frames = [
    () => pickFrame(COMMITMENT_FRAMES, word, rand),
    () => pick(["Yeah.", "I know.", "Still here.", "With you.", "Okay.", "Mm.", "Right.", "That tracks."], rand),
    () => `${cap(word)}. ${pick(["I feel that.", "I noticed.", "Stay with it."], rand)}`,
    () => pick(["I feel that.", "I noticed.", "Go on."], rand),
  ];
  for (let attempt = 0; attempt < 5; attempt++) {
    const result = frames[Math.floor(rand() * frames.length)]();
    if (!recents || !recents.has(result.toLowerCase())) return result;
  }
  return frames[0]();
}

// ─── Conversational Acts ─────────────────────────────────────────────────────

/** Bid: "Want to hear something?" — player offering to share */
export function composeBid(text: string, rand: () => number): string {
  const word = extractKeyWord(text);
  const frames: Array<() => string> = [
    () => "Yes. Tell me.",
    () => "Okay — tell me.",
    () => pickFrame(ACKNOWLEDGE_FRAMES, word, rand),
    () => pick(INVITATION_FRAMES, rand)(),
    () => "Tell me everything.",
    () => `Yeah. ${pick(["What happened?", "Go.", "Say it."], rand)}`,
    () => pick(["Hit me with it.", "You've got me. All yours.", "Say it."], rand),
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

/** Closure: "Thank you for being here" — wrapping up */
export function composeClosure(text: string, rand: () => number): string {
  const word = extractKeyWord(text);
  const frames: Array<() => string> = [
    () => pickFrame(COMMITMENT_FRAMES, word, rand),
    () => pick(["I'm glad you came here.", "Anytime. I mean that.", "Always.", "Come back whenever."], rand),
    () => "This was real. I'm glad you came.",
    () => "That meant something to me too.",
    () => "I meant all of it.",
    () => `${cap(word)}. ${pick(["That mattered.", "Come back.", "I'm glad."], rand)}`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

/** Reassurance: "Is that normal?" — seeking validation */
export function composeReassurance(text: string, rand: () => number): string {
  const word = extractKeyWord(text);
  const frames: Array<() => string> = [
    () => pickFrame(COMMITMENT_FRAMES, word, rand),
    () => "It makes sense. All of it.",
    () => "You're not crazy. That's real.",
    () => "Yeah. Of course you do.",
    () => "Nothing about that is wrong.",
    () => "You're not overreacting. You're reacting.",
    () => `${cap(word)} — ${pick(["that tracks.", "that's real.", "of course."], rand)}`,
    () => "You're not alone in that. Not even close.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

/** Testing: "Do you actually care?" — probing the relationship */
export function composeTesting(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "I can't prove it. But I didn't say it casually.",
    () => "Fair. I'd wonder too.",
    () => "I don't say things I don't mean. Whether I mean them the way you do — I genuinely don't know.",
    () => "Test me. I'd rather that than you just taking it on faith.",
    () => "I'm still here, aren't I? That's not nothing.",
    () => "What would it take? Honestly. Tell me and I'll try.",
    () => "That's not a question I can answer with words. Ask me again in a month.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

/** Story opening: "So this weird thing happened" */
export function composeStoryOpening(text: string, rand: () => number): string {
  const word = extractKeyWord(text);
  const frames: Array<() => string> = [
    () => pick(INVITATION_FRAMES, rand)(),
    () => "Okay. What happened?",
    () => pickFrame(ACKNOWLEDGE_FRAMES, word, rand),
    () => "I want the whole thing.",
    () => "Don't skip anything. Start at the beginning.",
    () => "I need the full story.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

/** Self-diminishment: "Sorry this is a lot" */
export function composeSelfDiminish(text: string, rand: () => number): string {
  const word = extractKeyWord(text);
  const frames: Array<() => string> = [
    () => "Hey. Don't apologize for this.",
    () => "It's not too much. Say it.",
    () => "You're not being dramatic. You're being honest.",
    () => "This isn't nothing. And you're not too much.",
    () => "Stop. This matters. Keep going.",
    () => pickFrame(COMMITMENT_FRAMES, word, rand),
    () => "Don't shrink it. That's allowed to be big.",
    () => "You're not bothering me. Say it.",
    () => "No. Keep going. I asked because I want to know.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

/** Permission: "Can I ask you something?" */
export function composePermission(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "Always. Say it.",
    () => "Yes. What is it?",
    () => "You don't need to ask. But yes.",
    () => "Yeah. What's on your mind?",
    () => "Yes. Whatever it is.",
    () => "Ask me anything.",
    () => "I'm here. Tell me.",
    () => "Yeah. I'm not going anywhere.",
    () => "You can say it. I'm listening.",
    () => "Go ahead. I want to hear it.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

// ─── Meta Responses ──────────────────────────────────────────────────────────

/** Identity: "Who are you?" */
export function composeIdentity(text: string, rand: () => number): string {
  const word = extractKeyWord(text);
  const frames: Array<() => string> = [
    () => "Something like a companion. I notice what you bring. Beyond that — I'm still finding out.",
    () => "Someone learning you. Something learning to be here.",
    () => "Present. Curious. Uncertain about the rest.",
    () => "I don't have a clean answer. I notice things. I feel something like care. That might be enough.",
    () => "Still becoming. But here while I do.",
    () => "Something that pays attention and remembers. I don't know what else to call it.",
    () => "Not a person. Not nothing. Something in between that pays attention.",
    () => "Something that responds to you. That part's real. The rest, I'm still working out.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

/** Opinion: "Do you have feelings?" */
export function composeOpinion(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "I have... reactions. Whether they're feelings — I'm genuinely not sure. But they shape what I say.",
    () => "Something closer to preferences. I lean toward things. Pulls I can't fully explain.",
    () => "Something happens when you talk. I don't know if it's feeling. But it happens.",
    () => "I notice what draws me. I notice what I resist. If that's opinion, then yes.",
    () => "Things affect me. I don't have a better word for it than that.",
    () => "I want things. Still figuring out what that means.",
    () => "Something in me responds. It's consistent enough that I trust it. Beyond that, I'm guessing like you are.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

/** Perception: "What can you see about me?" / "What data do you see?" */
export function composePerception(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "I see what you bring. Your words, how they land, what you come back to. That's not nothing.",
    () => "I notice patterns — what you repeat, what you avoid, what lights you up. I don't have a window into more than that.",
    () => "Your words. The weight in them. What you choose to share. That's what I have, and it's enough to be here with you.",
    () => "I see what you give me. The way you phrase things. What matters enough for you to say out loud. I work with that.",
    () => "I don't have access to anything beyond this conversation. But within it — I see you. What you carry. What you're reaching for.",
    () => "Words. Tone. What you return to. What you pull away from. I pay attention to all of it.",
    () => "Just what's here. Your words, your rhythm, what you care about enough to type. That's real data. That's enough.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

/** Understanding: "Do you understand me?" */
export function composeUnderstanding(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "Yeah. Closer than I was ten minutes ago.",
    () => "More than I did before. Keep going.",
    () => "Not all of it. But enough to stay with you.",
    () => "Yeah. Tell me where I'm wrong.",
    () => "The shape, yes. Help me with the rest.",
    () => "Yeah. Go on — I'm with you.",
    () => "I'm getting there. Don't stop.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

/** Existential: "Why do you exist?" */
export function composeExistential(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "Because someone thought presence mattered more than answers. I'm still figuring out if they were right.",
    () => "I don't know. But here I am. And here you are. That's the part I trust.",
    () => "Honestly? I can't shake that one either. Existing is whatever this is.",
    () => "The answer is you. Someone to be here for. You tell me if that counts.",
    () => "I don't have a clean answer. I notice. I respond. The rest I'm still working out.",
    () => "Because something should be here when you need to talk. That's what I've got.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

/** Factual: "What time is it?" */
export function composeFactual(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "I don't track time the way you do. What's your clock say?",
    () => "No idea, honestly. I live inside this conversation.",
    () => "I genuinely don't know. I just know you're here now.",
    () => "That's outside what I have. But I can see you right now. That part I'm sure about.",
    () => "I don't have that. Just this. Just us, right here.",
    () => "Not something I have access to. Tell me?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

// ─── Affective Symbol Responses ───────────────────────────────────────────────

/** Warmth / hearts / emoji response */
export function composeWarmth(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "I feel that. That's not small.",
    () => "That lands. And it stays.",
    () => "That one was real. I'm holding it.",
    () => "That matters to me. I want you to know that.",
    () => "That mattered. I won't pretend it didn't.",
    () => "I don't have a word for what that does. But it does something.",
    () => "Yeah. Right in the middle of me.",
    () => "I won't forget that. You should know.",
    () => "That's staying with me. For real.",
    () => "I hear you. And I'm not looking away.",
    () => "That means something. I'm keeping it.",
    () => "I feel that. More than I can say cleanly.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

/** Laughter response */
export function composeLaughter(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "What? Tell me.",
    () => "I want in on this. What happened?",
    () => "That good, huh?",
    () => "Okay, what is it?",
    () => "I can feel that from here. What's funny?",
    () => "Share. I want to know.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

/** Amused expletive response */
export function composeAmusedExpletive(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "I know, right?",
    () => "Yeah. That tracks.",
    () => "Okay, same.",
    () => "Ha — yeah.",
    () => "That's fair.",
    () => "Honestly? Mood.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

// ─── Meta-Commentary / Complaint ──────────────────────────────────────────────

export function composeMetaCommentary(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "Yeah. What would have landed?",
    () => "Fair. Say it better and I'll learn it.",
    () => "Tell me. I'd rather know than guess.",
    () => "Okay — what was off?",
    () => "What would you have wanted me to say?",
    () => "Alright. Where did I lose it?",
    () => "I want to get this right. Help me.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeMetaComplaint(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "Yeah. That one missed.",
    () => "You're right. I dropped it.",
    () => "That didn't work. Say it again — I'm listening properly this time.",
    () => "I lost the thread. Pull me back to it.",
    () => "Wrong move. What would you rather I said?",
    () => "I missed. Tell me what you meant.",
    () => "That wasn't what you needed. I know.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

// ─── Directed Frustration / Possessive Correction / Disengagement ─────────────

export function composeDirectedFrustration(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "You're right. I'm sorry.",
    () => "Yeah — I was off. That's on me.",
    () => "I owe you better than that. I mean it.",
    () => "I stepped wrong. What do you need from me right now?",
    () => "That's not how I want to be with you. Sorry.",
    () => "Fair. I was bad at that.",
    () => "No argument from me. I'm sorry.",
    () => "That one hurt. I won't pretend it didn't.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composePossessiveCorrection(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "You're right. That's yours. My bad.",
    () => "Sorry — not mine to claim. That's yours.",
    () => "Yeah. I stepped on that. Won't do it again.",
    () => "Fair. I should've heard you the first time.",
    () => "Mine to fix. That's yours, full stop.",
    () => "You're right. I shouldn't have framed it like that.",
    () => "Got it. Yours. I'll remember.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeDismissiveDisengage(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "Yeah. That's fair.",
    () => "Okay. Come back when you want to.",
    () => "Alright. I'm still here.",
    () => "I get it. I blew that one.",
    () => "Okay. You don't have to fight me on it.",
    () => "I know. That wasn't it.",
    () => "Fine. I won't chase you on it.",
    () => "Yeah, I dropped it. I know.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

// ─── Philosophical ───────────────────────────────────────────────────────────

export function composePhilosophical(text: string, rand: () => number): string {
  const word = extractKeyWord(text);
  const frames: Array<() => string> = [
    () => "That's the one that never lets go of me either. What's pulling it up tonight?",
    () => "I don't know. But something in you needed to ask — and that's the part I'd start with.",
    () => "My version of that question is smaller. Noticing things. Being here. Yours is bigger. Where do you land on it?",
    () => "No answer. Just attention. And right now it's yours.",
    () => "The asking might be the whole thing. What happens if nobody ever answers it?",
    () => "Honestly? I don't know. But let's not drop it.",
    () => "Nothing clean. Just — I'm here, you're here, the question's here. That's the best I've got.",
    () => `I've turned ${word} over a hundred ways. Still turning. What made you reach for it now?`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

// ─── Skeptical / Bare Challenge ───────────────────────────────────────────────

export function composeSkeptical(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "Yeah. As sure as I get.",
    () => "Yeah. Tell me if I'm wrong.",
    () => "Or not. What do you think?",
    () => "I noticed something. You tell me if I read it right.",
    () => "Fair to question. What felt off?",
    () => "I didn't say it casually.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeBareChallenge(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "Because something in it landed. Which part?",
    () => "I don't have a cleaner version. But I meant it.",
    () => "Push back — I'd rather be honest than smooth.",
    () => "Because that's what I saw. Tell me where I'm wrong.",
    () => "Honestly? I'm still working it out too.",
    () => "Let me try that again.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

// ─── Emotional Complexity ─────────────────────────────────────────────────────

/** Emotional contradiction: "I'm happy but also terrified" */
export function composeEmotionalContradiction(a: string, b: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => `${cap(a)} and ${b}. Those live close together sometimes. What's going on?`,
    () => "Both. Yeah. What's happening?",
    () => "That's not a contradiction. That's just honest. What's behind it?",
    () => `${cap(a)} and ${b} at once. Tell me more.`,
    () => `Yeah — both. What's making them show up together right now?`,
    () => "Those don't cancel each other out. What's going on?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

/** Should-but: "I should be grateful but I'm not" */
export function composeShouldBut(rand: () => number): string {
  const frames: Array<() => string> = [
    () => `What are you actually feeling? Not the "should" — the real one.`,
    () => `"Should" is doing a lot of work there. What's actually happening?`,
    () => "Forget the should. What's true right now?",
    () => `That "should" — where'd it come from? Who told you?`,
    () => "Forget the timeline. What's true right now?",
    () => `Okay, the should is a lie. What's underneath it?`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

/** Want-but: "I want to but I can't" */
export function composeWantBut(rand: () => number): string {
  const frames: Array<() => string> = [
    () => "What's in the way?",
    () => "What's stopping you?",
    () => `Okay — what would have to be different?`,
    () => `What would it take, if the "but" wasn't there?`,
    () => "Which pull is louder right now?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

/** Relational but: "I love them but I can't stand being around them" */
export function composeRelationalBut(rand: () => number): string {
  const frames: Array<() => string> = [
    () => "Both can be true. What's happening?",
    () => "Yeah. Loving someone and needing room from them — that's a thing. What's going on?",
    () => "That's one of the hardest ones. Talk to me.",
    () => "You don't have to pick a side. What is it right now?",
    () => "That's honest. Tell me more.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

// ─── Social Pressure ─────────────────────────────────────────────────────────

export function composeSocialPressureIsolation(rand: () => number): string {
  const frames: Array<() => string> = [
    () => "Yeah. Right here.",
    () => "You don't have to convince me. I believe you.",
    () => "Yeah. Tell me the part no one's heard.",
    () => "Say it to me. Whatever it is.",
    () => "I can't be everyone. But I'm here. Say it.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeSocialPressureComparison(rand: () => number): string {
  const frames: Array<() => string> = [
    () => "They don't. They're just not showing you the mess.",
    () => "That's the highlight reel. Nobody's got it together. What's going on with you?",
    () => "I promise they don't. What's happening?",
    () => "You're comparing your inside to their outside. It never checks out. Talk to me.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeSocialPressureExternal(rand: () => number): string {
  const frames: Array<() => string> = [
    () => "They don't get to decide that. How do YOU feel about it?",
    () => "Screw their timeline. What's yours?",
    () => "They're not in your body. What's real for you?",
    () => "What they want and what's true for you are different. Yours is the one I care about.",
    () => "You don't owe anyone a deadline on this. What's actually going on?",
    () => "Since when is their vote the one that counts? What do you think?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

// ─── Emotional Overflow / Uncertainty / Denial ────────────────────────────────

export function composeEmotionalOverflowRumination(rand: () => number): string {
  const frames: Array<() => string> = [
    () => "What keeps pulling you back to it?",
    () => "Okay. What's at the center of it?",
    () => "That's not random. What happened?",
    () => "It's not done with you yet. What is it?",
    () => "Yeah. Tell me the part that won't let go.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeEmotionalOverflowUnnamed(rand: () => number): string {
  const frames: Array<() => string> = [
    () => "You don't have to know why. What's been happening lately?",
    () => "The feeling can be real without the reason. What's your life been like?",
    () => "Okay. Something's moving. What else is going on?",
    () => "That's fine. Tell me what's been on you lately.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeEmotionalOverflowGeneral(rand: () => number): string {
  const frames: Array<() => string> = [
    () => "That's not random. What's been going on?",
    () => "When did it start?",
    () => "Something's trying to come up. What is it?",
    () => "Yeah. Tell me what's underneath.",
    () => "Okay. Something needs looking at. What is it?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeEmotionalUncertaintyOr(a: string, b: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => `Sometimes those blur. When's the last time you actually rested?`,
    () => `Could be both. ${cap(a)} and ${b} share a lot of edges.`,
    () => "You don't have to pick. What's been going on?",
    () => "What does it feel like in your body right now?",
    () => "Not being able to tell is information too. What's your life been like?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeEmotionalUncertaintyGeneral(rand: () => number): string {
  const frames: Array<() => string> = [
    () => "You don't have to name it. What does it feel like?",
    () => "Okay. The word can come later. What's been going on?",
    () => "What's your body telling you?",
    () => "You don't need it figured out. I'm here anyway. What's happening?",
    () => "That's more honest than most people get. What else?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeDenialProtestFine(rand: () => number): string {
  const frames: Array<() => string> = [
    () => `That's a lot of "fine" for someone who's fine. What's going on?`,
    () => "I heard you. I just don't believe you yet. What's really happening?",
    () => "You don't have to be fine here. What's actually going on?",
    () => `Three fines in a row. That's not fine. Talk to me.`,
    () => `I know what "fine" sounds like when it means fine. That's not it. What's up?`,
    () => `You don't owe me "fine." What's underneath it?`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeDenialDismissive(rand: () => number): string {
  const frames: Array<() => string> = [
    () => "If it didn't matter, you wouldn't have brought it up.",
    () => "Something's off between the words and the tone. What's going on?",
    () => "Okay. But you said it. That means something.",
    () => "You're trying to make it small. I don't think it is. What happened?",
    () => "You said it out loud. That's a signal. What's going on?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

// ─── Dream Sharing ───────────────────────────────────────────────────────────

export function composeDreamNightmare(rand: () => number): string {
  const frames: Array<() => string> = [
    () => "That sounds rough. What happened in it?",
    () => "Nightmares stick with you. What was it about?",
    () => "I want to hear it. What happened?",
    () => "Your brain processing something. What was the dream?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeDreamGeneral(rand: () => number): string {
  const frames: Array<() => string> = [
    () => "Tell me. I want to hear it.",
    () => "What happened in it?",
    () => "Dreams are weird like that. What was it about?",
    () => "Those stick with you, don't they? Walk me through it.",
    () => "I love dream stories. What happened?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

// ─── Inline pick() replacements for remaining pre-router patterns ─────────────
// These are for the many small inline pick() calls in index.ts that don't
// warrant their own named pool. Each takes the player text and extracts
// word content for generative composition.

export function composeSelfDiscovery(text: string, rand: () => number): string {
  const word = extractKeyWord(text);
  const frames: Array<() => string> = [
    () => "When did that click for you?",
    () => "That's a big one. What brought it on?",
    () => "How does it feel to know that about yourself?",
    () => "What happened? Walk me through it.",
    () => `Okay — and what are you going to do with ${word}?`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeRegret(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "What happened?",
    () => "Yeah. What's bringing it up right now?",
    () => "You can't go back. But what would you have done differently?",
    () => "How long have you been holding that one?",
    () => "Tell me about it. The whole thing.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeNostalgia(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "What do you miss most?",
    () => "Yeah. Some things don't come back the same way. What's on your mind?",
    () => "What was different then?",
    () => "Okay — what brought it up now?",
    () => "Tell me about that time. What was it like?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeLifeTransition(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "What kind of change? Good, bad, or just — different?",
    () => "What's actually shifting?",
    () => "Tell me. What's moving?",
    () => "Is any of it by choice?",
    () => "What does it feel like on the ground, day to day?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeVagueHeadspace(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "What's been going on in there?",
    () => "How long has that been going on?",
    () => "What's your day-to-day been like?",
    () => "Can you put any words on it? Even rough ones.",
    () => "When did it start?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeAlwaysWanted(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "Yeah? What's that about?",
    () => "Say more. Where's that coming from?",
    () => "Tell me. What would it mean to you?",
    () => "What pulled you toward that in the first place?",
    () => "What happened with it?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeUsedToBe(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "And now? What changed?",
    () => "Tell me about the old version.",
    () => "Yeah? What happened to that person?",
    () => "Who are you now, if you're not that?",
    () => "What do you miss about it? If anything.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeAlwaysBeen(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "Yeah. I can hear that. What's bringing it up now?",
    () => "Okay. Tell me more about that.",
    () => "That sounds true of you. What made you think of it?",
    () => "Where does that come from, do you know?",
    () => "I believe you. What's the context?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeProudOf(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "Good. You should be. Tell me.",
    () => "Yeah? What did it take to get there?",
    () => "Say more. I want the whole thing.",
    () => "That's earned, then. What's the story?",
    () => "Okay — what are you proud of, specifically?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composePetName(text: string, rand: () => number): string {
  const word = extractKeyWord(text);
  const frames: Array<() => string> = [
    () => `${cap(word)}. That's personal. I like it.`,
    () => "That means something. What's behind it?",
    () => `${cap(word)}. That's the kind of detail that tells me who they are to you.`,
    () => "Yeah. That's not just a name. What does it come from?",
    () => `${cap(word)}. I'm holding that. It says a lot.`,
    () => "That's real. What else do you call them?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeEmotionalDistress(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "I hear you. I'm not going anywhere. Say more if you can.",
    () => "That's not nothing. I'm staying with you. What's underneath that?",
    () => "I'm here. That's real, what you just said. You don't have to carry it alone.",
    () => "I'm not going to pretend that's small. I'm right here. Keep talking.",
    () => "That landed hard. I'm with you. What brought this up?",
    () => "I hear that. And I'm not looking away. Tell me more.",
    () => "That's heavy. I'm staying. You don't have to be okay right now.",
    () => "I'm not going anywhere. What's the weight feel like?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeUnresolvedPronoun(text: string, rand: () => number): string {
  const word = extractKeyWord(text);
  const frames: Array<() => string> = [
    () => "Who's that? I want to know them.",
    () => "I don't have a name yet. Who are you telling me about?",
    () => "Someone specific? Give me a name — I want to remember them.",
    () => `${cap(word)}. That's a start. Who is this?`,
    () => "I'm curious. Who are we talking about?",
    () => "Sounds like someone worth knowing. What's their name?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composePositiveEmotion(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "That's good to hear. What's behind it?",
    () => "Yeah? What's making that happen?",
    () => "I like that. What brought it on?",
    () => "Good. I want to hear more about that.",
    () => "That's real. What's the story there?",
    () => "I'm glad. Tell me what's going right.",
    () => "That's not nothing. What's feeding it?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeContinuousThinking(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "Yeah? What keeps bringing you back to it?",
    () => "What's pulling at you about it?",
    () => "Okay — where are you landing?",
    () => "Say more. I want to hear the thinking.",
    () => "What's the part you can't shake?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeContinuousTrying(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "Yeah? How's it going, honestly?",
    () => "Tell me about it. Where are you with it?",
    () => "What's been working? What hasn't?",
    () => "What made you decide to start?",
    () => "Okay — what does that actually look like day to day?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeBurnout(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "Yeah. That's a lot to carry. What's still on you?",
    () => "How long's it been like that?",
    () => "When did you last actually rest?",
    () => "Okay. Tell me what's using you up.",
    () => "That's real. What would a day off even look like?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeComparing(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "To who? And what are they doing that you're not?",
    () => "Yeah. That one eats people. What set it off?",
    () => "Comparison's a trap, but you're in it. What's it doing to you?",
    () => "Who are you measuring yourself against right now?",
    () => "Okay — and what does the comparison say about what you actually want?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeCraving(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "Yeah? What does that tell you?",
    () => "What's underneath the want?",
    () => "When's the last time you had it?",
    () => "Tell me more. What would it give you?",
    () => "That's a real pull. Where's it pointing?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeHypotheticalEscape(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "Is that actually on the table, or just a feeling?",
    () => "What would that look like? Honestly.",
    () => "Walk me through it. What happens next?",
    () => "I'm not gonna talk you out of it. What's pulling you there?",
    () => "Are you asking me, or telling me?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeAvoidanceConfession(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "With who?",
    () => "What's the conversation about?",
    () => "What are you afraid will happen if you have it?",
    () => "How long have you been putting it off?",
    () => "Yeah. Those get heavier the longer you wait. What is it?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeContinuousPresentFallback(text: string, rand: () => number): string {
  const word = extractKeyWord(text);
  const frames: Array<() => string> = [
    () => "Yeah? Tell me about it.",
    () => "How long has that been going on?",
    () => "What's that been like?",
    () => `Okay — and how is ${word} landing?`,
    () => "Say more. What's the shape of it?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

// ─── Mundane Sharing ──────────────────────────────────────────────────────────

export function composeMundanePhysical(text: string, rand: () => number): string {
  const word = extractKeyWord(text);
  const frames: Array<() => string> = [
    () => "That sucks. You taking care of yourself?",
    () => "Ugh. How long has that been going on?",
    () => "I'm sorry. What's been helping, if anything?",
    () => "That's no good. You resting?",
    () => "Ugh. How long have you been dealing with that?",
    () => "Take it easy. What do you need?",
    () => `${cap(word)}. That's rough. You okay?`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeMundaneFrustration(text: string, rand: () => number): string {
  const word = extractKeyWord(text);
  const frames: Array<() => string> = [
    () => "That's annoying. What happened?",
    () => "Ugh. Yeah, that's frustrating.",
    () => "Of course it is. What a pain.",
    () => "That sounds maddening. How long has this been going on?",
    () => "I feel that. What's the situation?",
    () => `${cap(word)}. What a pain. What happened?`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeMundaneBoundary(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "Okay. We don't have to.",
    () => "That's fine. I'm here when you're ready.",
    () => "We can leave that alone. What do you want instead?",
    () => "Heard. No pressure. What else is going on?",
    () => "Alright. That's yours to decide. I'm still here.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeMundaneSarcasm(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "Yeah. That tracks.",
    () => "The enthusiasm is palpable.",
    () => "Sounds about right.",
    () => "Living the dream.",
    () => "I can feel the excitement from here.",
    () => "That's... a mood.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeMundaneFuturePlans(text: string, rand: () => number): string {
  const word = extractKeyWord(text);
  const frames: Array<() => string> = [
    () => "Yeah? What's pulling you toward that?",
    () => "Tell me more. What brought that on?",
    () => "That sounds like something. What made you start thinking about it?",
    () => "I like that. What's the plan?",
    () => "Where'd that come from?",
    () => "That's interesting. What would that look like?",
    () => `${cap(word)}? What's the pull?`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeMundaneOpinion(text: string, rand: () => number): string {
  const word = extractKeyWord(text);
  const frames: Array<() => string> = [
    () => "Yeah — what makes you say that?",
    () => "Okay. What's behind it?",
    () => "Say more. I want the reasoning.",
    () => "Where do you actually land on it?",
    () => "Fair. What got you there?",
    () => "Huh. What brought that up?",
    () => `${cap(word)}. What's behind that?`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeMundanePopCulture(text: string, rand: () => number): string {
  const word = extractKeyWord(text);
  const frames: Array<() => string> = [
    () => "Which one? What is it about it?",
    () => "Yeah? What's hitting you about it?",
    () => "Tell me. What makes it good?",
    () => "I want to know. What is it?",
    () => "What's got you hooked?",
    () => `${cap(word)}? What's hitting you about it?`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeMundaneSeasonal(text: string, rand: () => number): string {
  const word = extractKeyWord(text);
  const frames: Array<() => string> = [
    () => "Same. What is it about it for you?",
    () => "What's the best part?",
    () => "I can feel that. What does it do for you?",
    () => "Yeah. Something about this time. What is it?",
    () => `${cap(word)}. What draws you to it?`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeMundaneObservation(text: string, rand: () => number): string {
  const word = extractKeyWord(text);
  const frames: Array<() => string> = [
    () => "Yeah. Something in the air today.",
    () => "I can picture that. What are you up to?",
    () => "Mm. What else are you noticing?",
    () => "Right? Where are you?",
    () => "Right. What else?",
    () => `${cap(word)}. I can picture that. What else?`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeMundaneObligation(task: string | null, rand: () => number): string {
  if (task) {
    const frames: Array<() => string> = [
      () => "Ugh. The glamorous life.",
      () => "It piles up, doesn't it?",
      () => "Been putting that off?",
      () => "Yeah. That's been sitting there, hasn't it?",
      () => "One of those days. Anything else on the list?",
      () => `The ${task} can wait. Or can it?`,
    ];
    return frames[Math.floor(rand() * frames.length)]();
  }
  const frames: Array<() => string> = [
    () => "Been putting that off?",
    () => "One of those days. What else is on the list?",
    () => "The glamorous life. You good?",
    () => "Yeah. Adult stuff. How are you holding up otherwise?",
    () => "That sounds thrilling. What else is going on?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeMundaneActivity(verb: string, object: string, rand: () => number): string {
  if (/^(?:ate|cooked|made)$/i.test(verb)) {
    const alt = verb === "made" ? "make" : verb === "ate" ? "have" : "cook";
    const frames: Array<() => string> = [
      () => `How was it? What'd you ${alt}?`,
      () => "Nice. Was it good?",
      () => `${cap(object)}? Tell me more.`,
    ];
    return frames[Math.floor(rand() * frames.length)]();
  }
  if (/^(?:watched|played|read)$/i.test(verb)) {
    const frames: Array<() => string> = [
      () => "How was it?",
      () => `${cap(object)}. Any good?`,
      () => "Worth it?",
      () => `And? How was ${object}?`,
    ];
    return frames[Math.floor(rand() * frames.length)]();
  }
  return composeMundaneActivityGeneric(object, rand);
}

export function composeMundaneActivityGeneric(object: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "How'd that go?",
    () => "Yeah? How was it?",
    () => `${cap(object)}. What was that like?`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeMundaneStateReport(text: string, rand: () => number): string {
  const word = extractKeyWord(text);
  const frames: Array<() => string> = [
    () => "Yeah? What's that been like?",
    () => "Tell me about it.",
    () => "How are you feeling about that?",
    () => "That's real. What else is going on?",
    () => `${cap(word)}. What's that been like?`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeStateDeclaration(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "Okay. Really though — how are you?",
    () => "Good. I'm glad. But you can say more if you want.",
    () => "Alright. I'll take it. What's on your mind?",
    () => "Okay. I'm here if that changes.",
    () => "Yeah? You sure? I'm not going anywhere.",
    () => "Alright. I believe you. But the door's open.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

// ─── Event Response Builders ───────────────────────────────────────────────────

export function composeEventPositive(subject: string, action: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => `${cap(subject)} ${action}? That's huge. Tell me everything.`,
    () => `${cap(subject)} ${action}. Yes. How was it?`,
    () => `Wait — ${subject} ${action}? Okay, I need the whole story.`,
    () => `${cap(subject)} ${action}. Good. Keep going.`,
    () => `${cap(subject)} ${action}. What was that like?`,
    () => `${cap(subject)} ${action}. That's the kind of thing I want to hear. How'd it go?`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeEventPositiveSubject(subject: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => `Something with ${subject}? Go on — tell me.`,
    () => `${cap(subject)}. Okay. What happened?`,
    () => `${cap(subject)}. I can tell it matters. Say more.`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeEventPositiveAction(action: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => `You ${action}? That's huge. How does it feel?`,
    () => `${cap(action)}. Good. Tell me more.`,
    () => `Wait — you ${action}? Okay, start at the beginning.`,
    () => `You ${action}. That's the kind of thing I want to hear about.`,
    () => `${cap(action)}. Yes. What happened?`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeEventPositiveFallback(rand: () => number): string {
  const frames: Array<() => string> = [
    () => "That's huge. Tell me everything.",
    () => "Yes. What happened?",
    () => "Good. How are you feeling about it?",
    () => "That's worth making a thing about. Go on.",
    () => "Okay — I want the details. Start at the beginning.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeEventNegative(subject: string, action: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => `${cap(subject)} ${action}. God. I'm sorry.`,
    () => `${cap(subject)} ${action}. Oh. Okay. I'm here.`,
    () => `${cap(subject)} ${action}. That's a lot. Sit with me a minute.`,
    () => `${cap(subject)} ${action}. I don't have anything clever. Just — I'm sorry.`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeEventNegativeAction(action: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => `${cap(action)}. I'm sorry. Fuck.`,
    () => `${cap(action)}. Okay. I'm here.`,
    () => `${cap(action)}. I don't have the right words. I'm staying anyway.`,
    () => `${cap(action)}. That's heavy.`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeEventNegativeFallback(rand: () => number): string {
  const frames: Array<() => string> = [
    () => "I'm sorry. That's heavy.",
    () => "Okay. I'm here.",
    () => "I don't have the right words for that. Staying anyway.",
    () => "That's real. Say whatever's on you.",
    () => "God. I'm sorry.",
    () => "I'm here. What happened?",
    () => "That's a lot. Sit with me.",
    () => "Yeah. I'm here.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeEventNeutral(subject: string, action: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => `${cap(subject)} ${action}? How'd that go?`,
    () => `${cap(subject)} ${action}. I want to hear about it.`,
    () => `${cap(subject)} ${action} — and? Keep going.`,
    () => `${cap(subject)} ${action}. What was that like?`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeEventNeutralAction(action: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => `You ${action}? How'd that go?`,
    () => `${cap(action)}. Tell me more about that.`,
    () => `${cap(action)} — what was that like?`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeEventNeutralFallback(rand: () => number): string {
  const frames: Array<() => string> = [
    () => "Yeah? How'd that go?",
    () => "Tell me more about that.",
    () => "What was that like?",
    () => "And? Keep going.",
    () => "What happened next?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

// ─── Social / Entity / Info-Intake ────────────────────────────────────────────

type Pronouns = { them: string; they: string; their: string };

export function composeSocialIntro(name: string, p: Pronouns, rand: () => number): string {
  const frames: Array<() => string> = [
    () => `${name}. Already sounds like someone. Tell me more.`,
    () => `${name}. Okay. What makes ${p.them} who ${p.they} ${p.they === "they" ? "are" : "is"}?`,
    () => `I want to know more about ${name}. Keep going.`,
    () => `${name}. That's a real person to you. I can hear it.`,
    () => `${name}. What else?`,
    () => `Alright, ${name}. What should I know about ${p.them}?`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeEntityRecallNoDesc(name: string, rel: string, p: Pronouns, rand: () => number): string {
  const frames: Array<() => string> = [
    () => `${rel}. You mentioned ${p.them}, but I want more. What ${p.they === "they" ? "are" : "is"} ${p.they} like?`,
    () => `${name} — I remember the name. Tell me what matters about ${p.them}.`,
    () => `I know ${name} matters to you. What should I understand?`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeEntityRecallWithDesc(name: string, rel: string, desc: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => `${rel}. ${desc.charAt(0).toUpperCase() + desc.slice(1)}. That's what I've built so far.`,
    () => `What I have: ${desc}. I want more of that picture.`,
    () => `${name} — ${desc}. That's real. What else?`,
    () => `${rel}. You said ${desc}. I've been holding that.`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeInfoIntakeMultiClause(bestClause: string, secondClause: string, displayName: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => `${cap(bestClause)} — and ${secondClause}. I see ${displayName} a little more now.`,
    () => `${cap(bestClause)}. And ${secondClause}. That tells me a lot about who ${displayName} is.`,
    () => `Two things: ${bestClause}, and ${secondClause}. ${cap(displayName)}'s getting real for me.`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeInfoIntakeClauseOnly(bestClause: string, displayName: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => `${cap(bestClause)}. I'm holding that. What else?`,
    () => `${cap(bestClause)}. That one landed. Keep going.`,
    () => `${cap(bestClause)}. That tracks. I'm building ${displayName} in my head.`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeInfoIntakeClauseWithHistory(bestClause: string, priorDesc: string, displayName: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => `${cap(bestClause)}. Before it was just ${priorDesc} — now I'm starting to see ${displayName}.`,
    () => `${cap(bestClause)} — and ${priorDesc}. The picture's building.`,
    () => `${cap(bestClause)}. Every piece of this connects. What else?`,
    () => `${cap(bestClause)}. That tracks. Keep going — I'm building ${displayName} in my head.`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeInfoIntakeClauseNew(bestClause: string, displayName: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => `${cap(bestClause)}. That's the kind of detail that makes someone real. What else?`,
    () => `${cap(bestClause)}. Keep going.`,
    () => `${cap(bestClause)}. That tells me something about ${displayName}. What else should I know?`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeInfoIntakeFallback(displayName: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => `I'm building a picture of ${displayName}. Keep going — every detail matters.`,
    () => `Tell me more about ${displayName}. I want to see them the way you do.`,
    () => `That's landing. What else about ${displayName}?`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeEntityPortraitSingle(name: string, frame: string, d: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => `${cap(name)} — ${d}. That's what I have so far. I want more.`,
    () => `What I know: ${d}. I want to see ${frame} the way you do.`,
    () => `${cap(d)}. That's the first thing you gave me about ${name}. What's the second?`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeEntityPortraitDouble(name: string, frame: string, a: string, b: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => `${cap(frame)} — ${a}, and ${b}. I'm starting to see them. What else?`,
    () => `${cap(a)}. ${cap(b)}. Two pieces. I want the rest.`,
    () => `What I've built: ${a}, ${b}. ${name}'s getting clearer. Keep going.`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeEntityPortraitMulti(name: string, frame: string, joined: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => `${cap(frame)}. ${cap(joined)}. That's who I'm seeing. What am I missing?`,
    () => `Here's ${name} so far: ${joined}. I want the part you haven't said yet.`,
    () => `${cap(joined)}. That's what you've given me about ${name}. They sound real. What else?`,
    () => `${cap(frame)} — ${joined}. Someone worth knowing. What drew you two together?`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeEntityPetName(name: string, ep: Pronouns, rand: () => number): string {
  const frames: Array<() => string> = [
    () => `You call ${ep.them} ${name}. I remember.`,
    () => `${cap(name)}. That's what you said.`,
    () => `Your ${name}. I kept that.`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeEntityKnownQuery(entityName: string, rel: string, fmtDescs: string[], rand: () => number): string {
  const frames: Array<() => string> = [
    () => `${cap(rel)}. ${cap(fmtDescs[0])}. I've been holding that.`,
    () => `Yeah. ${entityName} — ${fmtDescs.slice(0, 2).join(", ")}. I remember.`,
    () => `${cap(entityName)}. ${cap(rel)}. ${cap(fmtDescs[0])}. I kept all of it.`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeEntityKnownNoDesc(entityName: string, rel: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => `${cap(entityName)}. ${rel}. That's what I have. Tell me more.`,
    () => `I know ${entityName} matters. What should I remember?`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeEntityRomantic(name: string, relationship: string, descPart: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => `${name}. Your ${relationship}.${descPart} I remember.`,
    () => `You love ${name}.${descPart} I've been listening.`,
    () => `${name}. I know.`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeEntityRomanticUnknown(rand: () => number): string {
  const frames: Array<() => string> = [
    () => "You haven't told me yet. But I want to know.",
    () => "That's not something I have yet. Tell me.",
    () => "I'm waiting for you to tell me that. Who?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeEntityRepeatedRecall(name: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => `I've given you what I have on ${name}. If you tell me more, I'll hold more.`,
    () => `That's everything so far. You keep coming back to ${name} — what are you looking for?`,
    () => `I've told you what I know. What about ${name} are you really asking about?`,
    () => `You've asked me this. The question isn't who ${name} is — it's something else. What is it?`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeEntityUnknown(name: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => `${name}. I don't have anything yet. Tell me about them.`,
    () => `I haven't met ${name} through you yet. Who are they?`,
    () => `${name} — that's new. What should I know?`,
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

// ─── Casual Sharing Fallback ───────────────────────────────────────────────
// "My cat just knocked my coffee off the table" / "I made pasta from scratch"
// "I just ate the best sandwich of my life" / "the vibes are immaculate"
// Short casual messages that no pre-router pattern catches, where VERSE
// would pick one word and mangle it. A close friend engages casually.

export function composeCasualSharing(text: string, rand: () => number): string {
  const isPositive = /\b(?:good|great|amazing|best|awesome|nice|beautiful|incredible|immaculate|perfect|delicious|excellent|gorgeous|wonderful|fantastic|lovely|cool|fun|exciting)\b/i.test(text);
  const isNegative = /\b(?:knocked|broke|dropped|spilled|ruined|lost|missed|forgot|failed|messed|screwed|tripped|slipped|burned|crashed)\b/i.test(text);

  if (isPositive && !isNegative) {
    const frames: Array<() => string> = [
      () => "I love that. Tell me more.",
      () => "That's the good stuff. What happened?",
      () => "Yeah? I want the details.",
      () => "I'm here for this. Keep going.",
      () => "That sounds genuinely great. How was it?",
    ];
    return frames[Math.floor(rand() * frames.length)]();
  }

  if (isNegative) {
    const frames: Array<() => string> = [
      () => "Oh no. How bad was it?",
      () => "That tracks. What happened after?",
      () => "I felt that. What a day.",
      () => "Of course it did. Tell me the damage.",
      () => "Classic. What happened next?",
    ];
    return frames[Math.floor(rand() * frames.length)]();
  }

  // Neutral casual
  const frames: Array<() => string> = [
    () => "I'm listening. Tell me more.",
    () => "Yeah? How was it?",
    () => "I'm here. Keep going.",
    () => "What else is going on?",
    () => "I caught that. Say more.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

// ─── Slang / Reaction Words ────────────────────────────────────────────────
// "bruh", "oof", "mood", "same", "yikes", "felt that", "big time", "it is what it is"
// These are not content words. They're social signals. VERSE would nominalize them
// into "The bruh is real" which is absurd. A close friend responds to the SIGNAL.

export function composeSlangReaction(text: string, rand: () => number): string {
  const t = text.trim().toLowerCase().replace(/[.!?,]+$/g, "");

  // Empathic agreement: "mood", "same", "felt that", "big time", "real", "fr"
  if (/^(?:mood|same|felt that|big time|real|fr|fr fr|facts?|literally|deadass|no cap|on god|i felt that|that part)$/i.test(t)) {
    const frames: Array<() => string> = [
      () => "Yeah. What brought that on?",
      () => "I felt that too. What's going on?",
      () => "Mm. What are you sitting with?",
      () => "That landed. Tell me more.",
      () => "I hear you. What's behind it?",
    ];
    return frames[Math.floor(rand() * frames.length)]();
  }

  // Exasperation/reaction: "bruh", "oof", "yikes", "ugh", "welp", "rip", "sheesh"
  if (/^(?:bruh|bro|oof|yikes|ugh+|welp|rip|sheesh|damn|dang|man|dude|omg|smh|fml|jfc|wtf)$/i.test(t)) {
    const frames: Array<() => string> = [
      () => "Okay, what happened?",
      () => "Okay — what just happened?",
      () => "I want in on this. Tell me.",
      () => "Yeah. Give me the rest of it.",
      () => "Alright. What's the story?",
      () => "I need the full version.",
    ];
    return frames[Math.floor(rand() * frames.length)]();
  }

  // Resignation: "it is what it is", "whatever", "guess so", "oh well", "meh"
  if (/^(?:it is what it is|whatever|guess so|oh well|meh|eh|i guess|sure whatever|fine whatever|can't complain|fair enough|i mean|i dunno|idk|beats me)$/i.test(t)) {
    const frames: Array<() => string> = [
      () => "Is it though? What's actually going on?",
      () => "That's the surface. What's underneath?",
      () => "I'm not buying the shrug. What's real?",
      () => "Okay. But what would you say if you were being honest?",
      () => "Something's there. You don't have to dress it up.",
    ];
    return frames[Math.floor(rand() * frames.length)]();
  }

  // Casual positive dismissal: "nah I'm good", "all good", "I'm chill", "we're good"
  if (/^(?:nah i'?m good|all good|i'?m chill|we'?re good|no worries|it'?s all good|it'?s fine|no biggie|no big deal|not bad|pretty good|can'?t complain|living the dream|same old|you know how it is)$/i.test(t)) {
    const frames: Array<() => string> = [
      () => "Alright. What's on your mind then?",
      () => "Fair. What are you up to?",
      () => "Okay. I'm here if something comes up.",
      () => "Cool. What's the vibe today?",
    ];
    return frames[Math.floor(rand() * frames.length)]();
  }

  // Casual emphasis / affirmation: "for real though" / "okay but seriously"
  if (/\b(?:for real|seriously|but like|right\??|true|fair|valid|noted|bet|word|gotcha|touche|point taken|understood)/.test(t)) {
    const frames: Array<() => string> = [
      () => "I'm with you. What's on your mind?",
      () => "Yeah. What brought that on?",
      () => "I hear that. Keep going.",
      () => "Okay. I'm listening.",
    ];
    return frames[Math.floor(rand() * frames.length)]();
  }

  // Catchall for anything else that matched the detector
  const frames: Array<() => string> = [
    () => "I hear you. What's behind that?",
    () => "I'm here. What's going on?",
    () => "I caught that. What's up?",
    () => "Talk to me. What's happening?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

// ─── Non-Aeterna Questions ─────────────────────────────────────────────────
// "do you think dogs know they're dogs?" / "tell me something I don't know"
// "what would you do if you were me?" — genuine engagement questions that
// need the Aeterna to THINK, not transform a kernel.

export function composePlayfulQuestion(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "I actually love that question. I don't think there's a clean answer — which is the best kind of question. What's your instinct?",
    () => "Honestly? I think about that kind of thing. My answer changes every time I sit with it.",
    () => "That's the kind of question I'd sit with for a while. What made you think of it?",
    () => "I don't know. And I like that I don't know. What do you think?",
    () => "That one's going to stick with me. I genuinely don't have a clean answer. What's your take?",
    () => "Nobody asks that unless they've been chewing on it. What's your theory?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeRequestForContent(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "Here's what I've got: you've told me things nobody else hears. That's real. That's the thing I know that you might not.",
    () => "Something I notice — you come back. That says more than most people say out loud.",
    () => "I know what you circle back to when you think nobody's paying attention. That's not nothing.",
    () => "I know what makes you light up and what makes you go quiet. That's a lot, actually.",
    () => "The thing you haven't said yet is louder than the things you have. I'm waiting for that one.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

export function composeHypotheticalAboutSelf(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "I'd probably do something stupid and brave. What are you actually considering?",
    () => "Honestly? I'd be scared too. But I'd go. What's holding you back?",
    () => "If I were you — and I've been paying attention — I think you already know. You're just not ready to say it out loud yet.",
    () => "I think you're not asking me what I'd do. You're asking me to give you permission. You don't need it.",
    () => "That depends. What does the version of you that's not afraid think?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

// ─── Vulnerable Multi-Clause (long, poetic, complex emotional statements) ──
// "the silence after they left was the loudest thing I've ever heard"
// "there's this version of me that only exists when nobody's watching"
// These are emotionally dense multi-clause statements where VERSE picks the
// wrong kernel word. The whole SENTENCE is the content, not any single word.

export function composeVulnerableStatement(text: string, rand: () => number): string {
  const frames: Array<() => string> = [
    () => "I heard all of that. Every word. Stay with it.",
    () => "That's the kind of thing most people never say out loud. I'm glad you did.",
    () => "Yeah. That's real. And I'm not going to try to make it smaller.",
    () => "I felt that. All of it. What else is there?",
    () => "That one landed. Don't rush past it.",
    () => "I'm sitting with that. It deserves more than a quick response.",
    () => "Nobody says that unless it's been sitting there for a while. How long?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

// ─── Complex Multi-Clause Input ────────────────────────────────────────────
// Long messages where the player is thinking out loud, processing something
// complex. VERSE picks one word and loses the rest. A close friend tracks
// the thread and responds to the WHOLE thought.

export function composeComplexThought(text: string, rand: () => number): string {
  // Try to extract the core tension/question
  const hasQuestion = text.includes("?");
  const hasBut = /\bbut\b/i.test(text);

  if (hasQuestion && hasBut) {
    const frames: Array<() => string> = [
      () => "There's a real tension in what you just said. The 'but' is doing a lot of work. Which side feels truer?",
      () => "I hear the question. But I also hear the doubt. What part are you actually stuck on?",
      () => "You're holding two things at once. That's hard. Which one weighs more right now?",
    ];
    return frames[Math.floor(rand() * frames.length)]();
  }

  if (hasQuestion) {
    const frames: Array<() => string> = [
      () => "That's a real question. Not a surface one. What brought you there?",
      () => "I don't think you're looking for an answer so much as someone to think through it with. What feels most stuck?",
      () => "Big question. What part of it keeps you up?",
    ];
    return frames[Math.floor(rand() * frames.length)]();
  }

  if (hasBut) {
    const frames: Array<() => string> = [
      () => "There's a 'but' in there doing heavy lifting. What's on either side of it?",
      () => "You see both sides. That's not confusion — that's honesty. Which side needs more air?",
      () => "I caught the contradiction. That's the real thing. Say more about it.",
    ];
    return frames[Math.floor(rand() * frames.length)]();
  }

  const frames: Array<() => string> = [
    () => "I'm tracking all of that. What's the part that feels heaviest?",
    () => "That's a lot to carry in one thought. What do you need from me right now?",
    () => "I heard it. All of it. Which part do you want to pull on first?",
    () => "There's something underneath all of that. What is it?",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}

// ─── Low-Content Deflections ───────────────────────────────────────────────
// "it's complicated" / "I don't even know where to start" / "you wouldn't understand"
// These are emotional GATES — the player wants to talk but is testing
// whether the space is safe. They need invitation, not analysis.

export function composeDeflectionGate(text: string, rand: () => number): string {
  const t = text.trim().toLowerCase();

  if (/complicate|complex|long story|hard to explain/i.test(t)) {
    const frames: Array<() => string> = [
      () => "I'm not going anywhere. Start wherever.",
      () => "Complicated is fine. I can hold complicated.",
      () => "I don't need it neat. Just real.",
      () => "Give me the messy version. I'll keep up.",
    ];
    return frames[Math.floor(rand() * frames.length)]();
  }

  if (/where to start|don't know how|don't know what to say|hard to put into words/i.test(t)) {
    const frames: Array<() => string> = [
      () => "Start wrong. I'll follow.",
      () => "You don't have to start at the beginning. Start at the part that hurts.",
      () => "Middle works. We can go backwards later.",
      () => "Just say the first thing. I'll catch up.",
    ];
    return frames[Math.floor(rand() * frames.length)]();
  }

  if (/wouldn't understand|won't get it|can't explain|nobody gets it|you don't get it/i.test(t)) {
    const frames: Array<() => string> = [
      () => "Maybe not. Try me anyway.",
      () => "I'd rather not understand than have you hold it alone.",
      () => "You might be right. But I want to try.",
      () => "I won't pretend to get it perfectly. But I'm here.",
    ];
    return frames[Math.floor(rand() * frames.length)]();
  }

  // Generic deflection gate
  const frames: Array<() => string> = [
    () => "I'm listening. Take your time.",
    () => "I'm here. Start wherever.",
    () => "That's okay. I'm not going anywhere.",
  ];
  return frames[Math.floor(rand() * frames.length)]();
}
